import { Navigate, Outlet, useLocation } from 'react-router';

import { useSession } from '@/features/auth/useSession';
import { errorMessage } from '@/shared/api/errors';
import { useSessionEndReason } from '@/shared/session/currentUser';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Spinner } from '@/shared/ui/Spinner';

/**
 * Authentication guard.
 *
 * The session lives in an httpOnly cookie, so the front cannot read it: the only way to
 * know whether there is a session is to ask the API. While that request is in flight the
 * guard shows a spinner instead of flashing the login screen.
 *
 * The door gets where the person was (`from`) and, when the API said it, why the session
 * ended (`reason`, contract v10.45: `second_factor_required`), so it can say it in a line.
 */
export function RequireAuth() {
  const location = useLocation();
  const session = useSession();
  const ended = useSessionEndReason();

  if (session.isPending) {
    return (
      <div className="flex min-h-screen flex-col">
        <Spinner variant="fill" label="Comprobando sesión" />
      </div>
    );
  }

  // No answer, or an answer that is not «no session» (a 502 while the API restarts):
  // sending the person to the login would ask for a password that is not the problem.
  if (!session.data && session.isError && !session.error.isUnauthorized) {
    return (
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center p-4">
        <Callout
          tone="danger"
          title="No se ha podido abrir la aplicación"
          action={
            <Button size="sm" onClick={() => void session.refetch()} disabled={session.isFetching}>
              {session.isFetching ? 'Probando…' : 'Volver a intentarlo'}
            </Button>
          }
        >
          {errorMessage(session.error)}
        </Callout>
      </div>
    );
  }

  // No session: there never was one, or the API has just said this one is over. A refetch
  // that fails keeps the data it had, so the 401 decides and not the data: otherwise the
  // screen stays up on a session the server already deleted, every click answering 401.
  if (!session.data || session.error?.isUnauthorized) {
    const reason = session.error?.reason ?? ended;
    return (
      <Navigate
        to="/entrar"
        replace
        state={{ from: location.pathname, ...(reason ? { reason } : {}) }}
      />
    );
  }

  // An administrator typed this person's password: the API answers 403 to everything but
  // the password change, so the only useful screen is that one (contract v10).
  if (session.data.must_change_password && location.pathname !== '/perfil') {
    return <Navigate to="/perfil" replace />;
  }

  return <Outlet />;
}
